import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const AdminRoomBooking = () => {
    // Admin Flow: Allow changing Date/Duration here directly
    const [dateStr, setDateStr] = useState(new Date().toISOString().split('T')[0]);
    const [duration, setDuration] = useState(1);
    const [timeStr, setTimeStr] = useState("12:00 PM"); // Default Check-in time? Or use 12:00 PM

    // State for Flow
    const [step, setStep] = useState(1); // 1: Building, 2: Room Type, 3: Category, 4: Details
    const [selection, setSelection] = useState({
        building: null,
        roomType: null,
        category: null,
        room: null
    });

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [foundUser, setFoundUser] = useState(null);
    const [initialPhone, setInitialPhone] = useState('');

    // Data State
    const [availabilityData, setAvailabilityData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (dateStr) {
            fetchAvailability();
        }
    }, [dateStr, duration]);

    const fetchAvailability = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`/api/public/booking/availability?date=${dateStr}&duration=${duration}`);
            if (response.data.status === 'success') {
                setAvailabilityData(response.data.data);
            }
        } catch (err) {
            console.error(err);
            setError('Failed to fetch rooms');
        } finally {
            setLoading(false);
        }
    };

    const handleDirectSelect = (building, roomType, category) => {
        setSelection({ building, roomType, category, room: null });
        setShowModal(true);
    };

    const handleModalSuccess = (user, phone) => {
        setFoundUser(user);
        setInitialPhone(phone);
        setShowModal(false);
        setStep(4);
    };

    // --- Render Helpers ---
    const renderStep1 = () => (
        <div className="d-flex flex-column gap-4">
            {availabilityData.map((b) => (
                <div key={b.building_id} className="card shadow-sm border-0 overflow-hidden">
                    <div className="d-flex flex-column flex-md-row h-100">
                        {/* Image Section */}
                        <div style={{ width: '100%', maxWidth: '200px' }}>
                            <div className="bg-light h-100 d-flex align-items-center justify-content-center text-muted">
                                {b.building_image ? <img src={b.building_image} alt={b.building_name} style={{ width: '100%' }} /> : "No Image"}
                            </div>
                        </div>

                        {/* Content Section */}
                        <div className="flex-grow-1 d-flex flex-column flex-md-row">
                            <div className="p-3 d-flex align-items-center justify-content-start flex-grow-1 bg-white">
                                <h5 className="mb-0 text-dark fw-bold text-uppercase">{b.building_name}</h5>
                            </div>

                            {/* Availability & Price Grid */}
                            <div className="p-3 text-white d-flex align-items-center justify-content-center" style={{ backgroundColor: '#2e7d32', minWidth: '400px' }}>
                                <div className="d-flex flex-row w-100 h-100 gap-2">
                                    {b.room_types.map((rt) => (
                                        <div key={rt.room_type_id} className="flex-fill d-flex flex-column border rounded p-2 bg-dark bg-opacity-25">
                                            <strong className="text-warning text-center small text-uppercase mb-2">{rt.room_type_name}</strong>
                                            {rt.categories.map(cat => (
                                                <div
                                                    key={cat.category_name}
                                                    className="d-flex justify-content-between align-items-center bg-white bg-opacity-10 p-1 rounded mb-1"
                                                    style={{ cursor: cat.available_count > 0 ? 'pointer' : 'not-allowed', fontSize: '0.9rem' }}
                                                    onClick={() => cat.available_count > 0 && handleDirectSelect(b, rt, cat)}
                                                >
                                                    <span>{cat.category_name}</span>
                                                    <span className="badge bg-light text-dark">{cat.available_count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            {availabilityData.length === 0 && !loading && <div className="text-center p-5 text-muted">No availability found.</div>}
        </div>
    );

    const [bookingDetails, setBookingDetails] = useState(null);

    return (
        <div className="container py-5">
            <h2 className="mb-4 text-center">CRO Booking / Walk-in</h2>

            {/* Search Controls */}
            {step < 4 && (
                <div className="card p-3 mb-4 shadow-sm">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-4">
                            <label className="form-label">Check-in Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={dateStr}
                                onChange={(e) => setDateStr(e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Duration (Days)</label>
                            <select className="form-select" value={duration} onChange={e => setDuration(parseInt(e.target.value))}>
                                <option value="1">1 Day</option>
                                <option value="2">2 Days</option>
                                <option value="3">3 Days</option>
                            </select>
                        </div>
                        <div className="col-md-4">
                            <button className="btn btn-primary w-100" onClick={fetchAvailability}>Check Availability</button>
                        </div>
                    </div>
                </div>
            )}

            {step === 1 && renderStep1()}

            {step === 4 && bookingDetails && (
                <div className="alert alert-success text-center">
                    <h4>Booking Created Successfully!</h4>
                    <p>Booking ID: {bookingDetails.booking_id}</p>
                    <p>Room to be allocated at Check-in.</p>
                    <button className="btn btn-primary" onClick={() => window.location.reload()}>New Booking</button>
                </div>
            )}

            {step === 4 && !bookingDetails && (
                <GuestDetailsForm
                    selection={selection}
                    dateStr={dateStr}
                    duration={duration}
                    onSuccess={setBookingDetails}
                    initialUser={foundUser}
                    initialPhone={initialPhone}
                />
            )}

            <BookingModal
                show={showModal}
                onClose={() => setShowModal(false)}
                selection={selection}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
}

// --- Sub Components (Simplified versions of BookingRoom.js components) ---

const BookingModal = ({ show, onClose, selection, onSuccess }) => {
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);

    if (!show || !selection) return null;

    const handleSearch = async () => {
        if (phone.length !== 10) return alert("Invalid Phone");
        setLoading(true);
        try {
            const res = await axios.get(`/api/public/user/phone/${phone}`);
            if (res.data.status === 'success') {
                onSuccess(res.data.data, phone);
            }
        } catch (err) {
            // Not found - Pass blank user
            onSuccess(null, phone);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Customer Check</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <p>Selected: <strong>{selection.category.category_name}</strong> in {selection.building.building_name}</p>
                        <input type="text" className="form-control mb-3" placeholder="Mobile Number" value={phone} onChange={e => setPhone(e.target.value)} />
                        <button className="btn btn-primary w-100" onClick={handleSearch} disabled={loading}>{loading ? 'Checking...' : 'Proceed'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const GuestDetailsForm = ({ selection, dateStr, duration, onSuccess, initialUser, initialPhone }) => {
    // Initialize Dates
    const initCheckIn = dateStr ? `${dateStr}T12:00` : new Date().toISOString().slice(0, 16);
    const d = new Date(dateStr || new Date());
    d.setDate(d.getDate() + (parseInt(duration) || 1));
    const initCheckOut = `${d.toISOString().split('T')[0]}T11:00`;

    const [form, setForm] = useState({
        // Booking Details
        check_in_time: initCheckIn,
        check_out_time: initCheckOut,
        allocated_room_no: '',
        room_rent: selection.category.price * (parseInt(duration) || 1),

        // User Details
        first_name: initialUser?.first_name || '',
        last_name: initialUser?.last_name || '',
        full_name: initialUser?.full_name || '', // Helper, but backend might need first/last
        phone_num: initialUser?.phone_num || initialPhone || '',
        email_id: initialUser?.email_id || '',
        address: initialUser?.address || '',
        town: initialUser?.town || '',
        state: initialUser?.state || 'Karnataka',
        country: initialUser?.country || 'India',
        postal_code: initialUser?.postal_code || '',
        id_proof_type: initialUser?.id_proof_type || '1', // 1: Aadhaar
        id_proof_number: initialUser?.id_proof_number || ''
    });

    // Populate from InitialUser if available (User Search Result)
    useEffect(() => {
        if (initialUser) {
            setForm(prev => ({
                ...prev,
                first_name: initialUser.first_name || '',
                last_name: initialUser.last_name || '',
                full_name: initialUser.full_name || `${initialUser.first_name || ''} ${initialUser.last_name || ''}`.trim(),
                phone_num: initialUser.phone_num || initialPhone,
                email_id: initialUser.email_id || '',
                address: initialUser.address || '',
                town: initialUser.town || '',
                state: initialUser.state || 'Karnataka',
                country: initialUser.country || 'India',
                postal_code: initialUser.postal_code || '',
                id_proof_type: initialUser.id_proof_type || '1',
                id_proof_number: initialUser.id_proof_number || ''
            }));
        }
    }, [initialUser, initialPhone]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // 1. Create/Update User
            let userId = initialUser?._id;

            // Construct User Payload
            const userPayload = {
                first_name: form.first_name,
                last_name: form.last_name,
                full_name: `${form.first_name} ${form.last_name}`,
                email_id: form.email_id || 'no-email@cro.com',
                phone_num: form.phone_num,
                address: form.address,
                town: form.town,
                state: form.state,
                country: form.country,
                postal_code: form.postal_code,
                id_proof_type: form.id_proof_type,
                id_proof_number: form.id_proof_number,
                password: 'CROUSER123' // Dummy for auto-created
            };

            if (!userId) {
                // Check if user exists first (Safety check if Modal skipped)
                try {
                    const check = await axios.get(`/api/public/user/phone/${form.phone_num}`);
                    if (check.data.status === 'success') {
                        userId = check.data.data._id;
                        // Ideally update him? 
                    }
                } catch (e) { /* User not found, proceed to create */ }

                if (!userId) {
                    const uRes = await axios.post('/api/public/users', userPayload);
                    if (uRes.data.status === 'success') userId = uRes.data.data._id;
                    else throw new Error("Failed to create user");
                }
            } else {
                // Update User? Yes, update details provided here.
                await axios.put(`/api/public/users/${userId}`, userPayload);
            }

            // 2. Create Booking
            // Backend expects: room_id, allocated_room_no, room_rent, id_type, id_number...
            // room_id in backend logic: if Category Key (string with |), it finds a room.
            // If Manual Room No provided, we pass it.

            // Construct Booking Payload
            const bookingPayload = {
                room_id: `${selection.building.building_id}|${selection.roomType.room_type_id}|${selection.category.category_name}`, // Category Key logic
                allocated_room_no: form.allocated_room_no,
                room_rent: form.room_rent,

                // User Snapshot (Required by Schema/Controller)
                name: userPayload.full_name,
                phone_num: form.phone_num,
                address: `${form.address}, ${form.town}`,
                id_type: form.id_proof_type,
                id_number: form.id_proof_number,
                email_id: form.email_id,

                // Dates
                date_time: form.check_in_time, // controller maps to booking_date
                check_out_date: form.check_out_time,

                // CRO Specific: No image upload, marking as Verified
                id_proof: 'Verified by CRO (' + form.id_proof_type + ')',

                // User Link
                user_id: userId,

                // Extra metadata for user creation inside controller (fallback)
                town: form.town,
                state: form.state,
                country: form.country,
                postal_code: form.postal_code,
                first_name: form.first_name,
                last_name: form.last_name
            };

            // Note: Controller expects multipart/form-data if uploading ID Proof. 
            // Since we are referencing existing or text ID, we might need to handle 'id_proof' image.
            // Current controller: `const id_proof = req.file ? ...`. 
            // It REQUIRES `id_proof` path if not file? 
            // Line 773: `if (!... || !id_proof ...)`
            // Wait, if no file, `id_proof` is null. And validation fails!
            // We need to send a dummy ID Proof path or handle it.
            // If user already has one, we can pass it? 
            // Hack: Provide a default string if no file uploaded. 
            // Since we are using JSON (axios.post default), we can't upload file easily unless we use FormData.
            // Let's assume we use FormData to be safe, or just mock the file string if we are "CRO" and verified physically.

            // Construct FormData
            const fd = new FormData();
            Object.keys(bookingPayload).forEach(key => fd.append(key, bookingPayload[key]));
            // Add a dummy id_proof string if no file content? 
            // Controller checks `req.file`. If not present, `id_proof` var is null.
            // BUT, if we put `id_proof` in body, does it take it? 
            // Line 771: `const id_proof = req.file ? ... : null`. 
            // It ignores body `id_proof`. 
            // We MUST fix Controller to accept body `id_proof` string (e.g. existing) OR we must upload a file.
            // Or we modify Controller to be lenient for Admin.
            // MODIFYING CONTROLLER IS SAFER. 
            // Wait, line 812 use `id_proof_image: id_proof`.

            // Let's check Controller again. 
            // Line 771: `const id_proof = req.file ? ...`. It DOES NOT look at req.body.id_proof.
            // This is a blocker.
            // I will first modify `AdminRoomBooking.jsx` to prepare the form, 
            // AND I will have to modify `rooms.js` to accept `id_proof` from body (e.g. "Verified Physically" or existing URL).

            // For now, I'll send it as FormData and just append a string 'id_proof' 
            // BUT logic at 771 overwrites it to null if no req.file. 

            // Let's proceed with Form creation first.
            const bRes = await axios.post('/api/public/booking/create', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (bRes.data.status === 'success') {
                alert("Booking Created Successfully!");
                onSuccess(bRes.data.data || { booking_id: 'NEW' });
            }

        } catch (err) {
            console.error(err);
            alert("Error: " + (err.response?.data?.message || err.message));
        }
    };

    return (
        <div className="card shadow p-4">
            <h4 className="mb-4 text-primary">Booking Details</h4>
            <form onSubmit={handleSubmit}>
                <div className="row g-3">
                    {/* Booking Specifics */}
                    <div className="col-md-3">
                        <label className="form-label fw-bold">Room Category</label>
                        <input type="text" className="form-control" value={selection.category.category_name} disabled />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label fw-bold">Manual Room No.</label>
                        <input type="text" name="allocated_room_no" className="form-control"
                            placeholder="e.g. 101"
                            value={form.allocated_room_no} onChange={handleChange} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label fw-bold">Check-In Time</label>
                        <input type="datetime-local" name="check_in_time" className="form-control"
                            value={form.check_in_time} onChange={handleChange} required />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label fw-bold">Check-Out Time</label>
                        <input type="datetime-local" name="check_out_time" className="form-control"
                            value={form.check_out_time} onChange={handleChange} required />
                    </div>

                    <div className="col-md-4">
                        <label className="form-label fw-bold">Total Rent (₹)</label>
                        <input type="number" name="room_rent" className="form-control"
                            value={form.room_rent} onChange={handleChange} required />
                    </div>

                    <div className="col-12"><hr /></div>

                    {/* Guest Details */}
                    <h5 className="mb-3 text-secondary">Guest Information</h5>

                    <div className="col-md-4">
                        <label className="form-label">Phone Number *</label>
                        <input type="text" name="phone_num" className="form-control" value={form.phone_num} onChange={handleChange} required maxLength="10" />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">First Name *</label>
                        <input type="text" name="first_name" className="form-control" value={form.first_name} onChange={handleChange} required />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Last Name</label>
                        <input type="text" name="last_name" className="form-control" value={form.last_name} onChange={handleChange} />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">Email ID</label>
                        <input type="email" name="email_id" className="form-control" value={form.email_id} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Address *</label>
                        <input type="text" name="address" className="form-control" value={form.address} onChange={handleChange} required />
                    </div>

                    <div className="col-md-4">
                        <label className="form-label">Town/City *</label>
                        <input type="text" name="town" className="form-control" value={form.town} onChange={handleChange} required />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">State</label>
                        <input type="text" name="state" className="form-control" value={form.state} onChange={handleChange} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">Postal Code</label>
                        <input type="text" name="postal_code" className="form-control" value={form.postal_code} onChange={handleChange} />
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">ID Proof Type</label>
                        <select name="id_proof_type" className="form-select" value={form.id_proof_type} onChange={handleChange}>
                            <option value="1">Aadhaar Card</option>
                            <option value="2">PAN Card</option>
                            <option value="3">Driving License</option>
                            <option value="4">Passport</option>
                            <option value="5">Voter ID</option>
                        </select>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">ID Proof Number *</label>
                        <input type="text" name="id_proof_number" className="form-control" value={form.id_proof_number} onChange={handleChange} required />
                    </div>

                    <div className="col-12 mt-4 text-center">
                        <button type="submit" className="btn btn-success btn-lg px-5">Confirm & Book</button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AdminRoomBooking;
